import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import useOverwatchAccess from '../../hooks/useOverwatchAccess';
import { API_URL } from '../../utils/api';
import SusScoreBadge from './SusScoreBadge';
import CheatDetectionInfo from './CheatDetectionInfo';
import './Overwatch.css';

/**
 * Hidden cheat-detection dashboard. Lists suspicion reports and top suspects.
 * Access is gated on ``OverwatchAccess``; users without access are redirected
 * to the home page instead of seeing an error (the page should feel
 * non-existent to unauthorised users).
 */
export default function OverwatchDashboard() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasAccess, canManage, loading: accessLoading } = useOverwatchAccess();
  const { getAuthHeaders } = useAuth();

  const [reports, setReports] = useState([]);
  const [thresholds, setThresholds] = useState({ review_threshold: 3, notify_threshold: 6 });
  const [leaderboard, setLeaderboard] = useState([]);
  const [analysisRuns, setAnalysisRuns] = useState([]);
  const [notifications, setNotifications] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [minScore, setMinScore] = useState(0);
  const [loading, setLoading] = useState(true);

  const [reportsSort, setReportsSort] = useState({ key: 'sus_score', dir: 'desc' });
  const [leaderboardSort, setLeaderboardSort] = useState({ key: 'rolling_avg_sus_score', dir: 'desc' });
  const [runsSort, setRunsSort] = useState({ key: 'analyzed_at', dir: 'desc' });

  const [demoMatchId, setDemoMatchId] = useState('');
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoMessage, setDemoMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        status: statusFilter,
        mark_viewed: '1',
      });
      if (minScore > 0) {
        params.set('min_score', String(minScore));
      }
      const [r1, r2, r3, r4] = await Promise.all([
        fetch(`${API_URL}/api/overwatch/reports?${params.toString()}`, {
          headers: getAuthHeaders(),
        }),
        fetch(`${API_URL}/api/overwatch/leaderboard`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/overwatch/notifications`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/overwatch/analysis-runs`, { headers: getAuthHeaders() }),
      ]);
      if (r1.ok) {
        const data = await r1.json();
        setReports(data.results || []);
        if (data.thresholds) setThresholds(data.thresholds);
      }
      if (r2.ok) setLeaderboard((await r2.json()).results || []);
      if (r3.ok) setNotifications(await r3.json());
      if (r4.ok) setAnalysisRuns((await r4.json()).results || []);
    } catch (err) {
      console.error('Failed to load overwatch data', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, statusFilter, minScore]);

  useEffect(() => {
    if (!accessLoading && !hasAccess) {
      navigate('/', { replace: true });
      return;
    }
    if (hasAccess) fetchAll();
  }, [hasAccess, accessLoading, navigate, fetchAll]);

  const triggerDemoAnalysis = useCallback(async (body) => {
    setDemoBusy(true);
    setDemoMessage(null);
    try {
      const res = await fetch(`${API_URL}/api/overwatch/analyze-demos`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        setDemoMatchId('');
        setDemoMessage(t('overwatch.queuedDemo', { count: data.queued }));
      } else {
        const err = await res.json().catch(() => ({}));
        setDemoMessage(err.detail || t('overwatch.queueDemoFailed'));
      }
    } catch {
      setDemoMessage(t('overwatch.serverCallError'));
    } finally {
      setDemoBusy(false);
    }
  }, [getAuthHeaders, t]);

  if (accessLoading || !hasAccess) {
    return null;
  }

  const toggleSort = (setter, current, key) => {
    if (current.key === key) {
      setter({ key, dir: current.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      setter({ key, dir: 'desc' });
    }
  };

  const sortArrow = (sort, key) => {
    if (sort.key !== key) return <span className="sort-arrow inactive">↕</span>;
    return <span className="sort-arrow active">{sort.dir === 'asc' ? '▲' : '▼'}</span>;
  };

  const compareValues = (a, b, dir) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') {
      return dir === 'asc' ? a - b : b - a;
    }
    const aStr = String(a).toLowerCase();
    const bStr = String(b).toLowerCase();
    if (aStr < bStr) return dir === 'asc' ? -1 : 1;
    if (aStr > bStr) return dir === 'asc' ? 1 : -1;
    return 0;
  };

  const sortBy = (arr, sort, getValue) => {
    return [...arr].sort((a, b) => compareValues(getValue(a, sort.key), getValue(b, sort.key), sort.dir));
  };

  const reportsValue = (r, key) => {
    switch (key) {
      case 'sus_score': return r.sus_score ?? 0;
      case 'player_name': return r.player_name || r.steam_id || '';
      case 'match_id': return r.match_id ?? 0;
      case 'map_name': return r.map_name || t('overwatch.mapFallback', { n: r.map_number || '' });
      case 'flags': return (r.flags || []).length;
      case 'review_status': return r.review_status || '';
      case 'analyzed_at': return r.analyzed_at ? new Date(r.analyzed_at).getTime() : 0;
      default: return '';
    }
  };

  const leaderboardValue = (row, key) => {
    switch (key) {
      case 'rolling_avg_sus_score': return row.rolling_avg_sus_score ?? 0;
      case 'player_name': return row.player_name || row.steam_id || '';
      case 'matches_analyzed': return row.matches_analyzed ?? 0;
      case 'flag_totals': {
        const totals = row.flag_totals || {};
        return Object.values(totals).reduce((sum, n) => sum + n, 0);
      }
      default: return '';
    }
  };

  const runsValue = (run, key) => {
    switch (key) {
      case 'match_id': return run.match_id ?? 0;
      case 'analyzed_at': return run.analyzed_at ? new Date(run.analyzed_at).getTime() : 0;
      case 'players_analyzed': return run.players_analyzed ?? 0;
      case 'maps_analyzed': return run.maps_analyzed ?? 0;
      case 'reports_created': return run.reports_created ?? 0;
      case 'had_demo': return run.had_demo ? 1 : 0;
      case 'outcome': return run.outcome || '';
      default: return '';
    }
  };

  const sortedReports = sortBy(reports, reportsSort, reportsValue);
  const sortedLeaderboard = sortBy(leaderboard, leaderboardSort, leaderboardValue);
  const sortedRuns = sortBy(analysisRuns, runsSort, runsValue);

  return (
    <div className="overwatch-page">
      <h1>🛡️ Overwatch</h1>
      <p className="overwatch-subtitle">
        {t('overwatch.dashSubtitle')}
      </p>

      <CheatDetectionInfo
        reviewThreshold={thresholds.review_threshold}
        notifyThreshold={thresholds.notify_threshold}
      />

      {notifications && (
        <div className="overwatch-stats-grid">
          <div className="overwatch-stat-card">
            <div className="label">{t('overwatch.important', { threshold: notifications.notify_threshold })}</div>
            <div className="value">{notifications.unseen_important}</div>
          </div>
          <div className="overwatch-stat-card">
            <div className="label">{t('overwatch.totalPending')}</div>
            <div className="value">{notifications.total_pending}</div>
          </div>
          <div className="overwatch-stat-card">
            <div className="label">{t('overwatch.topSuspects')}</div>
            <div className="value">{leaderboard.length}</div>
          </div>
          {canManage && (
            <div
              className="overwatch-stat-card"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/overwatch/admin')}
            >
              <div className="label">{t('overwatch.admin')}</div>
              <div className="value" style={{ fontSize: '1rem' }}>{t('overwatch.manageAccess')}</div>
            </div>
          )}
        </div>
      )}

      <div className="overwatch-filters">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="pending">{t('overwatch.filterPending')}</option>
          <option value="flagged">{t('overwatch.filterFlagged')}</option>
          <option value="cleared">{t('overwatch.filterCleared')}</option>
          <option value="">{t('matches.all')}</option>
        </select>
        <input
          type="number"
          min="0"
          max="10"
          step="0.5"
          value={minScore}
          onChange={(e) => setMinScore(parseFloat(e.target.value) || 0)}
          placeholder={t('overwatch.minScorePlaceholder')}
        />
        <button className="overwatch-refresh-btn" onClick={fetchAll}>
          {t('overwatch.refresh')}
        </button>
      </div>

      <div className="score-scale-bar">
        <div className="score-scale-segment tier-clean" style={{ flex: thresholds.review_threshold }}>
          <span className="score-scale-label">0 – {thresholds.review_threshold}</span>
          <span className="score-scale-name">{t('overwatch.scaleNotCreated')}</span>
        </div>
        <div className="score-scale-segment tier-watch" style={{ flex: thresholds.notify_threshold - thresholds.review_threshold }}>
          <span className="score-scale-label">{thresholds.review_threshold} – {thresholds.notify_threshold}</span>
          <span className="score-scale-name">{t('overwatch.scaleInvestigate')}</span>
        </div>
        <div className="score-scale-segment tier-sus" style={{ flex: 8 - thresholds.notify_threshold }}>
          <span className="score-scale-label">{thresholds.notify_threshold} – 8</span>
          <span className="score-scale-name">{t('overwatch.cdBandSusLabel')}</span>
        </div>
        <div className="score-scale-segment tier-cheat" style={{ flex: 2 }}>
          <span className="score-scale-label">8 – 10</span>
          <span className="score-scale-name">{t('overwatch.scaleLikelyCheat')}</span>
        </div>
      </div>

      {loading ? (
        <div className="overwatch-empty">{t('live.loading')}</div>
      ) : reports.length === 0 ? (
        <div className="overwatch-empty">{t('overwatch.noReportsMatch')}</div>
      ) : (
        <table className="overwatch-table sortable">
          <thead>
            <tr>
              <th onClick={() => toggleSort(setReportsSort, reportsSort, 'sus_score')}>{t('overwatch.colScore')} {sortArrow(reportsSort, 'sus_score')}</th>
              <th onClick={() => toggleSort(setReportsSort, reportsSort, 'player_name')}>{t('nav.players')} {sortArrow(reportsSort, 'player_name')}</th>
              <th onClick={() => toggleSort(setReportsSort, reportsSort, 'match_id')}>{t('nav.match')} {sortArrow(reportsSort, 'match_id')}</th>
              <th onClick={() => toggleSort(setReportsSort, reportsSort, 'map_name')}>{t('overwatch.colMap')} {sortArrow(reportsSort, 'map_name')}</th>
              <th onClick={() => toggleSort(setReportsSort, reportsSort, 'flags')}>{t('overwatch.colFlags')} {sortArrow(reportsSort, 'flags')}</th>
              <th onClick={() => toggleSort(setReportsSort, reportsSort, 'review_status')}>{t('overwatch.colStatus')} {sortArrow(reportsSort, 'review_status')}</th>
              <th onClick={() => toggleSort(setReportsSort, reportsSort, 'analyzed_at')}>{t('overwatch.colAnalyzed')} {sortArrow(reportsSort, 'analyzed_at')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedReports.map((r) => (
              <tr key={r.id} onClick={() => navigate(`/overwatch/report/${r.id}`)}>
                <td><SusScoreBadge score={r.sus_score} /></td>
                <td>{r.player_name || r.steam_id}</td>
                <td>#{r.match_id}</td>
                <td>{r.map_name || t('overwatch.mapFallback', { n: r.map_number })}</td>
                <td>
                  <div className="flag-chips">
                    {(r.flags || []).map((f) => (
                      <span key={f} className="flag-chip">{f}</span>
                    ))}
                  </div>
                </td>
                <td>
                  <span className={`review-status ${r.review_status}`}>{r.review_status}</span>
                </td>
                <td>{r.analyzed_at ? new Date(r.analyzed_at).toLocaleString('sv-SE') : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {leaderboard.length > 0 && (
        <>
          <h2 className="overwatch-section-heading">
            {t('overwatch.mostSuspected')}
            <span className="overwatch-section-subtitle">
              {t('overwatch.mostSuspectedSub')}
            </span>
          </h2>
          <table className="overwatch-table sortable">
            <thead>
              <tr>
                <th onClick={() => toggleSort(setLeaderboardSort, leaderboardSort, 'rolling_avg_sus_score')}>{t('overwatch.colScore')} {sortArrow(leaderboardSort, 'rolling_avg_sus_score')}</th>
                <th onClick={() => toggleSort(setLeaderboardSort, leaderboardSort, 'player_name')}>{t('nav.players')} {sortArrow(leaderboardSort, 'player_name')}</th>
                <th onClick={() => toggleSort(setLeaderboardSort, leaderboardSort, 'matches_analyzed')}>{t('overwatch.analyzedMatches')} {sortArrow(leaderboardSort, 'matches_analyzed')}</th>
                <th onClick={() => toggleSort(setLeaderboardSort, leaderboardSort, 'flag_totals')}>{t('overwatch.colCommonFlags')} {sortArrow(leaderboardSort, 'flag_totals')}</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeaderboard.map((row) => (
                <tr
                  key={row.steam_id}
                  onClick={() => navigate(`/overwatch/player/${row.steam_id}`)}
                >
                  <td><SusScoreBadge score={row.rolling_avg_sus_score} /></td>
                  <td>{row.player_name || row.steam_id}</td>
                  <td>{row.matches_analyzed}</td>
                  <td>
                    <div className="flag-chips">
                      {Object.entries(row.flag_totals || {})
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 4)
                        .map(([f, count]) => (
                          <span key={f} className="flag-chip">{f} ×{count}</span>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h2 className="overwatch-section-heading">
        {t('overwatch.analyzedMatches')}
        <span className="overwatch-section-subtitle">
          {t('overwatch.analyzedMatchesSub')}
        </span>
      </h2>

      <div className="overwatch-filters">
        <input
          type="number"
          min="1"
          value={demoMatchId}
          onChange={(e) => setDemoMatchId(e.target.value)}
          placeholder={t('overwatch.matchIdPlaceholder')}
          disabled={demoBusy}
        />
        <button
          className="overwatch-refresh-btn"
          disabled={demoBusy || !demoMatchId}
          onClick={() => triggerDemoAnalysis({ match_id: Number(demoMatchId) })}
        >
          {t('overwatch.analyzeDemoForMatch')}
        </button>
        <button
          className="overwatch-refresh-btn"
          disabled={demoBusy}
          onClick={() => triggerDemoAnalysis({ missing_only: true })}
        >
          {t('overwatch.analyzeMissingDemo')}
        </button>
      </div>
      {demoMessage && (
        <div className="overwatch-history-hint info">{demoMessage}</div>
      )}

      {analysisRuns.length === 0 ? (
        <div className="overwatch-empty">{t('overwatch.noRuns')}</div>
      ) : (
        <table className="overwatch-table sortable non-interactive-rows">
          <thead>
            <tr>
              <th onClick={() => toggleSort(setRunsSort, runsSort, 'match_id')}>{t('nav.match')} {sortArrow(runsSort, 'match_id')}</th>
              <th onClick={() => toggleSort(setRunsSort, runsSort, 'analyzed_at')}>{t('overwatch.colAnalyzed')} {sortArrow(runsSort, 'analyzed_at')}</th>
              <th onClick={() => toggleSort(setRunsSort, runsSort, 'players_analyzed')}>{t('nav.players')} {sortArrow(runsSort, 'players_analyzed')}</th>
              <th onClick={() => toggleSort(setRunsSort, runsSort, 'maps_analyzed')}>{t('overwatch.colMaps')} {sortArrow(runsSort, 'maps_analyzed')}</th>
              <th onClick={() => toggleSort(setRunsSort, runsSort, 'reports_created')}>{t('overwatch.colReports')} {sortArrow(runsSort, 'reports_created')}</th>
              <th onClick={() => toggleSort(setRunsSort, runsSort, 'had_demo')}>{t('overwatch.colSource')} {sortArrow(runsSort, 'had_demo')}</th>
              <th onClick={() => toggleSort(setRunsSort, runsSort, 'outcome')}>{t('overwatch.colOutcome')} {sortArrow(runsSort, 'outcome')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedRuns.map((run) => (
              <tr key={run.match_id}>
                <td>#{run.match_id}</td>
                <td>{run.analyzed_at ? new Date(run.analyzed_at).toLocaleString('sv-SE') : '—'}</td>
                <td>{run.players_analyzed}</td>
                <td>{run.maps_analyzed}</td>
                <td>{run.reports_created}</td>
                <td>
                  <span className={`analysis-source ${run.had_demo ? 'has-demo' : 'stats-only'}`}>
                    {run.had_demo ? t('overwatch.sourceDemo') : t('overwatch.sourceStatsOnly')}
                  </span>
                </td>
                <td>
                  <span className={`analysis-outcome ${run.outcome}`}>
                    {run.outcome === 'flagged' && t('overwatch.outcomeFlagged')}
                    {run.outcome === 'pending' && t('overwatch.outcomePending')}
                    {run.outcome === 'clean' && t('overwatch.outcomeClean')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
